import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FaRocket, FaArrowLeft, FaArrowRight, FaSave } from 'react-icons/fa';
import { agentsAPI, categoriesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import LoadingSpinner from '../components/ui/LoadingSpinner';

// Step Components
import WizardProgress from '../components/agent/WizardProgress';
import AgentFormStep1 from '../components/agent/AgentFormStep1';
import AgentFormStep2 from '../components/agent/AgentFormStep2';
import AgentFormStep3 from '../components/agent/AgentFormStep3';
import AgentFormStep4 from '../components/agent/AgentFormStep4';
import AgentFormStep5 from '../components/agent/AgentFormStep5';
import AgentFormStep6 from '../components/agent/AgentFormStep6';

/**
 * CreateAgent - Multi-step wizard for creating AI Agents
 */
const CreateAgent = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category_id: '',
    tags: '',
    system_prompt: '',
    tools: [],
    memory_type: 'short_term',
    max_context_window: 8192,
    example_interactions: [],
    is_published: true,
  });

  // Wizard steps configuration
  const steps = [
    { number: 1, name: 'Basic Info', component: AgentFormStep1 },
    { number: 2, name: 'System Prompt', component: AgentFormStep2 },
    { number: 3, name: 'Tools', component: AgentFormStep3 },
    { number: 4, name: 'Memory', component: AgentFormStep4 },
    { number: 5, name: 'Examples', component: AgentFormStep5 },
    { number: 6, name: 'Review', component: AgentFormStep6 },
  ];

  // Load categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Warn before leaving if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Fetch categories error:', error);
      toast.error('Failed to load categories');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    setHasUnsavedChanges(true);
    // Clear error for this field
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  // Validation for each step
  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1: // Basic Info
        if (!formData.title.trim()) {
          newErrors.title = 'Title is required';
        } else if (formData.title.length > 255) {
          newErrors.title = 'Title must be 255 characters or less';
        }
        if (!formData.description.trim()) {
          newErrors.description = 'Description is required';
        }
        if (!formData.category_id) {
          newErrors.category_id = 'Category is required';
        }
        break;

      case 2: // System Prompt
        if (!formData.system_prompt.trim()) {
          newErrors.system_prompt = 'System prompt is required';
        }
        break;

      case 3: // Tools (optional, no validation)
        break;

      case 4: // Memory Settings
        if (!formData.memory_type) {
          newErrors.memory_type = 'Memory type is required';
        }
        break;

      case 5: // Example Interactions (optional, no validation)
        break;

      case 6: // Review (no additional validation)
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps([...completedSteps, currentStep]);
      }
      if (currentStep < steps.length) {
        setCurrentStep(currentStep + 1);
        window.scrollTo(0, 0);
      }
    } else {
      toast.error('Please fix the errors before continuing');
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleStepClick = (stepNumber) => {
    // Only allow navigation to completed steps or the next step
    if (completedSteps.includes(stepNumber) || stepNumber < currentStep) {
      setCurrentStep(stepNumber);
      window.scrollTo(0, 0);
    }
  };

  const handleEditStep = (stepNumber) => {
    setCurrentStep(stepNumber);
    window.scrollTo(0, 0);
  };

  const buildAgentPayload = (publishOverride) => {
    const tagsArray = typeof formData.tags === 'string'
      ? formData.tags.split(',').map((tag) => tag.trim()).filter((tag) => tag.length > 0)
      : Array.isArray(formData.tags)
        ? formData.tags
        : [];

    const parsedMaxContext = parseInt(formData.max_context_window, 10);
    const isPublished = typeof publishOverride === 'boolean'
      ? publishOverride
      : Boolean(formData.is_published);

    const exampleInteractions = Array.isArray(formData.example_interactions)
      ? formData.example_interactions.filter(
          (example) => example?.user?.trim() || example?.assistant?.trim()
        )
      : [];

    return {
      title: formData.title.trim(),
      description: formData.description.trim(),
      systemPrompt: formData.system_prompt.trim(),
      iconUrl: formData.icon_url || undefined,
      tools: Array.isArray(formData.tools) ? formData.tools : [],
      memoryType: formData.memory_type,
      maxContextWindow: Number.isNaN(parsedMaxContext) ? 8192 : parsedMaxContext,
      exampleInteractions,
      categoryId: formData.category_id ? parseInt(formData.category_id, 10) : null,
      tags: tagsArray,
      isPublished,
    };
  };

  const handleSaveDraft = async () => {
    setLoading(true);
    try {
      const agentData = buildAgentPayload(false);

      const response = await agentsAPI.create(agentData);
      toast.success('Draft saved successfully!');
      setHasUnsavedChanges(false);
      navigate(`/agents/${response.data.agent.id}`);
    } catch (error) {
      console.error('Save draft error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to save draft';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Validate all required steps
    const requiredSteps = [1, 2, 4]; // Basic Info, System Prompt, Memory Settings
    for (const step of requiredSteps) {
      if (!validateStep(step)) {
        toast.error(`Please complete step ${step}: ${steps[step - 1].name}`);
        setCurrentStep(step);
        return;
      }
    }

    setLoading(true);
    try {
      const agentData = buildAgentPayload();

      const response = await agentsAPI.create(agentData);
      toast.success(formData.is_published ? 'Agent published successfully!' : 'Agent created as draft!');
      setHasUnsavedChanges(false);
      navigate(`/agents/${response.data.agent.id}`);
    } catch (error) {
      console.error('Create agent error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to create agent';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Check if user is authenticated
  if (!currentUser) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-bold mb-4">Authentication Required</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          You must be logged in to create an AI agent.
        </p>
        <Button onClick={() => navigate('/login')}>Log In</Button>
      </div>
    );
  }

  // Render current step component
  const CurrentStepComponent = steps[currentStep - 1].component;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2 text-gray-900 dark:text-spooky-ghost-white">
          Create AI Agent
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Build a custom AI agent with tools, memory, and personality. Follow the steps to configure your agent.
        </p>
      </div>

      {/* Progress Indicator */}
      <WizardProgress
        steps={steps}
        currentStep={currentStep}
        onStepClick={handleStepClick}
        completedSteps={completedSteps}
      />

      {/* Main Form Card */}
      <Card padding="spacious" className="mb-6">
        <CurrentStepComponent
          formData={formData}
          onChange={handleChange}
          categories={categories}
          errors={errors}
          onEditStep={handleEditStep}
        />
      </Card>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center gap-4">
        <div className="flex gap-3">
          {currentStep > 1 && (
            <Button
              type="button"
              variant="ghost"
              onClick={handlePrevious}
              disabled={loading}
            >
              <FaArrowLeft className="inline mr-2" />
              Previous
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate(-1)}
            disabled={loading}
          >
            Cancel
          </Button>
        </div>

        <div className="flex gap-3">
          {/* Save Draft Button (only show before final step) */}
          {currentStep < steps.length && (
            <Button
              type="button"
              variant="secondary"
              onClick={handleSaveDraft}
              disabled={loading}
              className="hidden sm:flex"
            >
              <FaSave className="inline mr-2" />
              Save Draft
            </Button>
          )}

          {/* Next/Submit Button */}
          {currentStep < steps.length ? (
            <Button
              type="button"
              variant="primary"
              onClick={handleNext}
              disabled={loading}
              className="bg-pink-500 hover:bg-pink-600"
            >
              Next
              <FaArrowRight className="inline ml-2" />
            </Button>
          ) : (
            <Button
              type="button"
              variant="primary"
              onClick={handleSubmit}
              disabled={loading}
              className="bg-pink-500 hover:bg-pink-600"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" className="inline mr-2" />
                  Creating...
                </>
              ) : (
                <>
                  <FaRocket className="inline mr-2" />
                  {formData.is_published ? 'Publish Agent' : 'Create Draft'}
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Progress Summary */}
      <div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
        Step {currentStep} of {steps.length}: {steps[currentStep - 1].name}
      </div>
    </div>
  );
};

export default CreateAgent;
